import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, ListOrdered, CheckCircle2, XCircle, Phone, AlertTriangle, ScrollText } from 'lucide-react';
export interface LogEntry {
  id: string;
  timestamp: number;
  type: 'route-update' | 'reroute' | 'alert-resolved' | 'alert-dismissed' | 'contact' | 'warning';
  message: string;
  detail?: string;
  truckId?: string;
}
interface LogsPanelProps {
  entries: LogEntry[];
}
const typeConfig: Record<LogEntry['type'], {
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
}> = {
  'route-update': {
    icon: ListOrdered,
    color: 'text-brand-blue',
    bg: 'bg-brand-blue/10',
    border: 'border-brand-blue/20'
  },
  reroute: {
    icon: Navigation,
    color: 'text-brand-green',
    bg: 'bg-brand-green/10',
    border: 'border-brand-green/20'
  },
  'alert-resolved': {
    icon: CheckCircle2,
    color: 'text-brand-green',
    bg: 'bg-brand-green/10',
    border: 'border-brand-green/20'
  },
  'alert-dismissed': {
    icon: XCircle,
    color: 'text-slate-400',
    bg: 'bg-slate-800/50',
    border: 'border-slate-700/50'
  },
  contact: {
    icon: Phone,
    color: 'text-slate-300',
    bg: 'bg-slate-800/50',
    border: 'border-slate-700/50'
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-brand-amber',
    bg: 'bg-brand-amber/10',
    border: 'border-brand-amber/20'
  }
};
function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
export function LogsPanel({
  entries
}: LogsPanelProps) {
  const sorted = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  if (sorted.length === 0) {
    return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-526">
        <ScrollText className="w-8 h-8 text-slate-700 mb-3" data-id="element-527" />
        <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-528">
          No activity yet
        </p>
        <p className="text-xs text-slate-500" data-id="element-529">
          Actions like route updates, reroutes, and alert resolutions will be
          logged here.
        </p>
      </div>;
  }
  return <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-530">
      <AnimatePresence initial={false} data-id="element-531">
        {sorted.map((entry, i) => {
        const config = typeConfig[entry.type];
        const Icon = config.icon;
        return <motion.div key={entry.id} initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300,
          delay: i < 3 ? i * 0.05 : 0
        }} className={`relative p-3 mb-2 last:mb-0 rounded-xl border ${config.bg} ${config.border}`} data-id="element-532">
              <div className="flex items-start gap-3" data-id="element-533">
                <div className={`mt-0.5 shrink-0 ${config.color}`} data-id="element-534">
                  <Icon className="w-4 h-4" data-id="element-535" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-536">
                  <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-537">
                    <p className="text-xs font-semibold text-slate-200 truncate" data-id="element-538">
                      {entry.message}
                    </p>
                    {entry.truckId && <span className="text-[10px] font-mono font-bold text-slate-400 shrink-0" data-id="element-539">
                        {entry.truckId}
                      </span>}
                  </div>
                  {entry.detail && <p className="text-[11px] text-slate-500 leading-snug line-clamp-2" data-id="element-540">
                      {entry.detail}
                    </p>}
                  <div className="flex items-center gap-2 mt-1.5" data-id="element-541">
                    <span className="text-[10px] font-mono text-slate-600" data-id="element-542">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                    <span className="text-[10px] text-slate-600" data-id="element-543">·</span>
                    <span className="text-[10px] text-slate-500" data-id="element-544">
                      {timeAgo(entry.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>;
      })}
      </AnimatePresence>
    </div>;
}